import { CSSProperties, ReactNode, useEffect } from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'
import { FinancialAccountWithAdminInfo } from '@/reducers/admin/financialAccountsReducer'
import { useHeardPlaid } from './helpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { Button, ButtonProps, ButtonVariant, Text } from '@/ui'

export const PlaidLink = ({
  account = null,
  redirectUri,
  redirectLink = '/accounts#connected-institutions',
  redirect = true,
  buttonVariant = 'primary',
  buttonSize,
  style,
  reconnectButtonText = 'Reconnect',
  connectButtonText = 'Connect an account',
  disconnected = false,
  className,
  iconLeft,
  shouldRedirectOnExit = true,
  onExitStatusChange,
  onError,
}: {
  account?: FinancialAccount | FinancialAccountWithAdminInfo | null
  redirectUri?: string
  redirectLink?: string
  redirect?: boolean
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonProps['size']
  style?: CSSProperties
  reconnectButtonText?: string | ReactNode
  connectButtonText?: string | ReactNode
  disconnected?: boolean
  className?: string
  iconLeft?: IconDefinition
  shouldRedirectOnExit?: boolean
  onExitStatusChange?: (exitStatus: string | null) => void
  onError?: (error: ErrorEvent | null) => void
}) => {
  const track = useAnalyticsTrack()

  const { open, ready, exitStatus, error } = useHeardPlaid({
    account,
    redirectLink: redirect ? redirectLink : '',
    redirectUri,
    disconnected,
    shouldRedirectOnExit,
  })

  useEffect(() => {
    onExitStatusChange?.(exitStatus || null)
  }, [exitStatus, onExitStatusChange])

  useEffect(() => {
    onError?.(error || null)
  }, [error, onError])

  const handleHasAccount = (
    account: FinancialAccount | FinancialAccountWithAdminInfo
  ) => {
    const eventName = disconnected
      ? 'clicked plaid reconnect'
      : 'clicked plaid add_remove'

    track(eventName, {
      fi_name: account.plaidInstitutionName ?? '',
    })

    open()
  }

  const handleNoAccount = () => {
    track('clicked new financial institution')
    open()
  }

  const handleClick = () => {
    if (account) {
      handleHasAccount(account)
    } else {
      handleNoAccount()
    }
  }

  return (
    <Button
      variant={buttonVariant}
      size={buttonSize}
      onClick={handleClick}
      disabled={!ready}
      style={style}
      className={className}
      iconLeft={iconLeft}
    >
      <Text className="text-current">
        {account ? reconnectButtonText : connectButtonText}
      </Text>
    </Button>
  )
}
